/**
 * IndiaMap — react-leaflet rewrite.
 *
 * Preserves all original features:
 *  - State view with per-state colour + click → district drill-down
 *  - Region view with district-level polygons grouped/coloured by RO
 *  - School markers (KV / PM SHRI) with tooltip, click → SchoolPopup
 *  - Legend counts (Total / PM SHRI / KV non-PM SHRI)
 *  - Show Markers toggle, Reset Zoom, State/Region mode toggle
 *  - Greying of non-selected states/regions
 *
 * Why react-leaflet:
 *  - MapContainer owns the DOM node — no more manual L.map() + mapRef juggling
 *  - GeoJSON / Marker as JSX → React handles re-renders declaratively
 *  - invalidateSize / fitBounds via useMap() hook — eliminates all setTimeout hacks
 *  - ResizeObserver on wrapper div handles tab-switch sizing
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  GeoJSON,
  Marker,
  Tooltip,
  useMap,
  ZoomControl,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { getAllSchoolCoordinates } from '../services/api';
import SchoolPopup from './SchoolPopup';
import { KVSLoader } from '../../../components/loaders';
import { apiGet } from '../../../shared/services/apiClient';
import { CSP_NONCE } from '../../../shared/services/cspNonce';

// Fix Leaflet bundler icon path
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom school icons
const kvIcon = L.icon({
  iconUrl: '/assets/kvMarker.svg',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  className: 'kv-image-icon',
  popupAnchor: [0, -6],
});

const pmIcon = L.icon({
  iconUrl: '/assets/PMShreeMarker.svg',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  className: 'pmshree-image-icon',
  popupAnchor: [0, -12],
});

// CSS injected once
const leafletStyles = `
  .leaflet-container { width: 100%; background: #f8f9fa; }
  .leaflet-control-zoom {
    right: 10px !important; left: auto !important; top: 10px !important;
    border: none !important; box-shadow: 0 1px 5px rgba(0,0,0,0.4) !important;
  }
  .leaflet-control-zoom a { background: white !important; color: #666 !important; border: none !important; }
  @keyframes fadeIn { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
  .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
  .leaflet-interactive { transition: fill 0.3s ease, stroke 0.3s ease, fill-opacity 0.3s ease; }
  .pmshree-image-icon { filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25)); transition: transform 0.18s ease, filter 0.18s ease; will-change: transform; transform-origin: center; }
  .pmshree-image-icon:hover { transform: scale(1.06); filter: drop-shadow(0 2px 4px rgba(0,0,0,0.32)); }
  .kv-image-icon { filter: drop-shadow(0 0.6px 1px rgba(0,0,0,0.12)); }
  .leaflet-marker-icon.kv-image-icon, .leaflet-marker-icon.pmshree-image-icon { display: block; transform-origin: center; cursor: pointer; }
`;

// India bounds and defaults
const INDIA_BOUNDS: L.LatLngBoundsExpression = [[4.0, 66.0], [37.5, 99.5]];
const INDIA_CENTER: L.LatLngExpression = [20.5937, 78.9629];
const INDIA_ZOOM = 5;

// State colour palette
const STATE_COLORS = [
  '#FFB6C1','#87CEEB','#98FB98','#DDA0DD','#F0E68C','#FFE4B5','#B0E0E6','#FFDAB9',
  '#E0BBE4','#C8E6C9','#FFCCBC','#B2DFDB','#F8BBD0','#C5CAE9','#D1C4E9','#BBDEFB',
  '#B3E5FC','#B2EBF2','#80DEEA','#80CBC4','#A5D6A7','#C5E1A5','#E6EE9C','#FFF59D',
  '#FFE082','#FFCC80','#FFAB91','#BCAAA4','#B0BEC5','#CFD8DC','#F48FB1','#CE93D8',
  '#9FA8DA','#90CAF9','#81D4FA','#80DEEA',
];
const getStateColor = (name?: string | null) =>
  name ? STATE_COLORS[name.charCodeAt(0) % STATE_COLORS.length] : '#CCCCCC';

// State to district GeoJSON mapping
const STATE_DISTRICT_MAP: Record<string, string> = {
  'Uttar Pradesh': '/data/states/uttar-pradesh.geojson',
  'Andaman and Nicobar': '/data/states/andaman-and-nicobar-islands.geojson',
  'Andaman and Nicobar Islands': '/data/states/andaman-and-nicobar-islands.geojson',
  'Andhra Pradesh': '/data/states/andhra-pradesh.geojson',
  'Arunachal Pradesh': '/data/states/arunachal-pradesh.geojson',
  'Assam': '/data/states/assam.geojson',
  'Bihar': '/data/states/bihar.geojson',
  'Chandigarh': '/data/states/chandigarh.geojson',
  'Chhattisgarh': '/data/states/chhattisgarh.geojson',
  'Dadra and Nagar Haveli and Daman and Diu': '/data/states/dnh-and-dd.geojson',
  'Dadra and Nagar Haveli, Daman and Diu': '/data/states/dnh-and-dd.geojson',
  'Daman and Diu': '/data/states/dnh-and-dd.geojson',
  'Delhi': '/data/states/delhi.geojson',
  'Goa': '/data/states/goa.geojson',
  'Gujarat': '/data/states/gujarat.geojson',
  'Haryana': '/data/states/haryana.geojson',
  'Himachal Pradesh': '/data/states/himachal-pradesh.geojson',
  'Jharkhand': '/data/states/jharkhand.geojson',
  'Karnataka': '/data/states/karnataka.geojson',
  'Kerala': '/data/states/kerala.geojson',
  'Lakshadweep': '/data/states/lakshadweep.geojson',
  'Madhya Pradesh': '/data/states/madhya-pradesh.geojson',
  'Maharashtra': '/data/states/maharashtra.geojson',
  'Manipur': '/data/states/manipur.geojson',
  'Meghalaya': '/data/states/meghalaya.geojson',
  'Mizoram': '/data/states/mizoram.geojson',
  'Nagaland': '/data/states/nagaland.geojson',
  'Orissa': '/data/states/odisha.geojson',
  'Odisha': '/data/states/odisha.geojson',
  'Puducherry': '/data/states/puducherry.geojson',
  'Punjab': '/data/states/punjab.geojson',
  'Rajasthan': '/data/states/rajasthan.geojson',
  'Sikkim': '/data/states/sikkim.geojson',
  'Tamil Nadu': '/data/states/tamil-nadu.geojson',
  'Telangana': '/data/states/telangana.geojson',
  'Tripura': '/data/states/tripura.geojson',
  'Uttarakhand': '/data/states/uttarakhand.geojson',
  'West Bengal': '/data/states/west-bengal.geojson',
  'Jammu and Kashmir': '/data/states/jammu-and-kashmir.geojson',
  'Ladakh': '/data/states/ladakh.geojson',
  'Union Territory of Ladakh': '/data/states/ladakh.geojson',
};

// Extract name from GeoJSON properties
const getFeatureStateName = (p: any) => p?.name || p?.NAME || p?.st_nm || p?.STATE_NAME || '';
const getFeatureDistrictName = (p: any) =>
  p?.Name || p?.name || p?.DISTRICT || p?.district || p?.dtname || '';

// Normalise district name for matching
const normalizeName = (s: string) =>
  s.toUpperCase().replace(/\s+/g, '').replace(/[.\-()&']/g, '').replace(/&/g, 'AND').trim();

const DISTRICT_VARIATIONS: Record<string, string[]> = {
  // Pre-existing aliases
  GAUTAMBUDDHANAGAR: ['GAUTAMBUDHNAGAR', 'NOIDA', 'GAUTAMBUDDHANAGAR'],
  GURGAON: ['GURUGRAM'],
  MUMBAI: ['BOMBAY'],
  KOLKATA: ['CALCUTTA'],
  CHENNAI: ['MADRAS'],
  THIRUVANANTHAPURAM: ['TRIVANDRUM'],
  KOCHI: ['COCHIN'],
  VADODARA: ['BARODA'],
  MYSURU: ['MYSORE'],
  BELAGAVI: ['BELGAUM'],
  VIJAYAPURA: ['BIJAPUR'],
  KALABURAGI: ['GULBARGA'],
  CHIKKAMAGALURU: ['CHIKMAGALUR'],
  SHIVAMOGGA: ['SHIMOGA'],
  TUMAKURU: ['TUMKUR'],
  BALLARI: ['BELLARY'],
  SOUTHANDAMANS: ['SOUTHANDAMAN'],
  NORTHANDMIDDLEANDAMAN: ['NORTHANDMIDDLEANDAMANS'],
  NICOBARS: ['NICOBAR'],

  // Gujarat
  KACHCHH: ['KUTCH'],

  // Karnataka
  BAGALKOT: ['BAGALKOTE'],
  CHAMARAJANAGAR: ['CHAMARAJANAGARA'],
  CHIKKABALLAPUR: ['CHIKKABALLAPURA'],

  // Madhya Pradesh
  BADWANI: ['BARWANI'],

  // Odisha
  BOLANGIR: ['BALANGIR'],
  KEONJHAR: ['KENDUJHAR'],
  SONEPUR: ['SUBARNAPUR'],

  // Tamil Nadu
  KANCHIPURAM: ['KANCHEEPURAM'],
  KANNIYAKUMARI: ['KANYAKUMARI'],
  CHENGALPET: ['CHENGALPATTU'],

  // Uttarakhand
  UDAMSINGHNAGAR: ['UDHAMSINGHNAGAR'],

  // Andhra Pradesh
  KADAPA: ['YSR'],
  SPSRNELLORE: ['SRIPOTTISRIRAMULUNELLORE'],

  // Telangana (Hanamakonda was carved from Warangal Urban in 2021)
  HANAMAKONDA: ['WARANGALURBAN'],

  // Jammu & Kashmir
  BADGAM: ['BUDGAM'],
  RAJAURI: ['RAJOURI'],

  // West Bengal
  '24PARAGANASNORTH': ['NORTH24PARGANAS'],
  DINAJPURDAKSHIN: ['DAKSHINDINAJPUR'],
  MALDAH: ['MALDA'],
  MEDINIPUREAST: ['PURBAMEDINIPUR'],
  MEDINIPURWEST: ['PASCHIMMEDINIPUR'],

  // Uttar Pradesh
  FAIZABAD: ['AYODHYA'],
  ALLAHABAD: ['PRAYAGRAJ'],
  SIDDARTHNAGAR: ['SIDDHARTHNAGAR'],

  // Maharashtra
  YAWATMAL: ['YAVATMAL'],

  // Bihar
  PASHCHIMCHAMPARAN: ['WESTCHAMPARAN'],
  PURVICHAMPARAN: ['EASTCHAMPARAN'],

  // Chhattisgarh
  KABIRDHAM: ['KABEERDHAM'],
  KOREA: ['KORIYA'],

  // Jharkhand
  EASTSINGHBUM: ['EASTSINGHBHUM'],
  KODARMA: ['KODERMA'],

  // Tripura
  UNAKOTI: ['UNOKOTI'],
};

const isDistrictMatch = (a: string, b: string): boolean => {
  const n1 = normalizeName(a), n2 = normalizeName(b);
  if (n1 === n2) return true;
  for (const [std, alts] of Object.entries(DISTRICT_VARIATIONS)) {
    if ((n1 === std && alts.includes(n2)) || (n2 === std && alts.includes(n1)) ||
        (alts.includes(n1) && alts.includes(n2))) return true;
  }
  const min = Math.min(n1.length, n2.length), max = Math.max(n1.length, n2.length);
  if (min >= 4 && max - min >= 3 && (n1.includes(n2) || n2.includes(n1))) return true;
  return false;
};

// Types
interface ROWiseData { roID: number; schoolCount: number; roName: string; }

export interface RegionData {
  name: string; value: number;
  state?: string; district?: string; region?: string; roID?: number;
}

export interface IndiaMapProps {
  geoJsonUrl?: string;
  districtGeoJsonUrl?: string;
  regionData?: RegionData[];
  onRegionClick?: (region: RegionData) => void;
  selectedRegion?: string | null;
  showDistricts?: boolean;
  mapMode?: 'state' | 'region';
  onModeChange?: (mode: 'state' | 'region') => void;
  filters?: { state?: string; region?: string; category?: string; kpi?: string; kpiLabel?: string };
  statesSchoolData?: Array<{ stateID: number; stateName: string; schoolCount: number }>;
  schoolCoordinates?: Array<{ schoolID?: number; schoolName?: string; latitude: number; longitude: number; isPMShree?: number; roName?: string; stateName?: string; districtName?: string; [key: string]: any }>;
  totalKVCount?: number;
  pmShreeCount?: number;
}

// Inner child that gives us imperative map access
function MapController({ onReady }: { onReady: (map: L.Map) => void }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}

// Main component
export default function IndiaMap({
  geoJsonUrl = '/data/india-states.geojson',
  regionData = [],
  onRegionClick,
  selectedRegion: selectedRegionProp,
  showDistricts = true,
  mapMode = 'state',
  onModeChange,
  filters = {},
  statesSchoolData = [],
  schoolCoordinates = [],
  totalKVCount = 0,
  pmShreeCount = 0,
}: IndiaMapProps) {
  // mode
  const [currentMode, setCurrentMode] = useState<'state' | 'region'>(mapMode);

  // GeoJSON data
  const [stateGeoJson, setStateGeoJson] = useState<any>(null);
  const [regionsData, setRegionsData] = useState<any>(null);
  const [districtGeoJson, setDistrictGeoJson] = useState<any>(null);

  // loading
  const [loading, setLoading] = useState(true);
  const [layerLoading, setLayerLoading] = useState(false);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [regionLayerLoading, setRegionLayerLoading] = useState(false);

  // selection
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  // hover
  const [hoveredState, setHoveredState] = useState<string | null>(null);

  // school markers
  const [allSchools, setAllSchools] = useState<any[]>([]);
  const [showMarkersManually, setShowMarkersManually] = useState(false);

  // region view
  const [regionViewGeoJson, setRegionViewGeoJson] = useState<any>(null);

  // RO-wise
  const [roWiseData, setROWiseData] = useState<ROWiseData[]>([]);

  // imperative map ref (populated by MapController)
  const mapInstanceRef = useRef<L.Map | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // popup
  const [selectedSchool, setSelectedSchool] = useState<any>(null);

  // ── Data loading ──────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [statesRes, regionsRes] = await Promise.all([
          fetch(geoJsonUrl),
          fetch('/data/regions.json'),
        ]);
        setStateGeoJson(await statesRes.json());
        setRegionsData(await regionsRes.json());
      } catch (e) {
        console.error('Failed to load map data:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [geoJsonUrl]);

  // RO-wise API
  useEffect(() => {
    apiGet('/kvis/kvprofile/rowise')
      .then((r: any) => {
        if (r.success) {
          setROWiseData(Array.isArray(r.data) ? r.data : []);
        }
      })
      .catch(() => {
        setROWiseData([]);
      });
  }, []);

  // School coordinates
  useEffect(() => {
    let cancelled = false;
    const supplied = Array.isArray(schoolCoordinates) && schoolCoordinates.length > 0;
    if (supplied) {
      setAllSchools(schoolCoordinates as any[]);
      return;
    }
    const load = async () => {
      setSchoolLoading(true);
      try {
        const data = await getAllSchoolCoordinates();
        if (!cancelled) setAllSchools(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load school coordinates:', e);
      } finally {
        if (!cancelled) setSchoolLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolCoordinates]);

  // Region view: build full district GeoJSON keyed by region
  useEffect(() => {
    if (currentMode !== 'region' || !regionsData) return;
    let cancelled = false;
    const loadRegionView = async () => {
      setRegionLayerLoading(true);
      try {
        const allFeatures: any[] = [];
        const seenPaths = new Set<string>();
        for (const [stateName, path] of Object.entries(STATE_DISTRICT_MAP)) {
          if (seenPaths.has(path as string)) continue;
          seenPaths.add(path as string);
          try {
            const res = await fetch(path as string);
            if (!res.ok) continue;
            const gj = await res.json();
            (gj.features || []).forEach((f: any) => {
              const dName = getFeatureDistrictName(f.properties);
              const region = regionsData.regions?.find((r: any) =>
                r.districts?.some((d: any) => {
                  if (typeof d === 'string') return isDistrictMatch(dName, d);
                  const nm = normalizeName(stateName), nm2 = normalizeName(d.stateName || '');
                  return isDistrictMatch(dName, d.districtName) &&
                    (nm === nm2 || nm.includes(nm2) || nm2.includes(nm));
                })
              );
              allFeatures.push({
                ...f,
                properties: {
                  ...f.properties,
                  regionName: region?.name ?? null,
                  regionColor: region?.color ?? '#696969',
                  regionId: region?.id ?? null,
                  isRegional: !!region,
                  stateName,
                  originalDistrictName: dName,
                },
              });
            });
          } catch { /* skip */ }
        }
        if (!cancelled) setRegionViewGeoJson({ type: 'FeatureCollection', features: allFeatures });
      } finally {
        if (!cancelled) setRegionLayerLoading(false);
      }
    };
    loadRegionView();
    return () => { cancelled = true; };
  }, [currentMode, regionsData]);

  // ResizeObserver
  useEffect(() => {
    if (!wrapperRef.current || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(() => { mapInstanceRef.current?.invalidateSize(); });
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, []);

  // ── loadStateDistricts ──────────────────────────────────────────────
  const loadStateDistricts = useCallback(async (stateName: string) => {
    const path = STATE_DISTRICT_MAP[stateName];
    if (!path) return;
    setLayerLoading(true);
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const gj = await res.json();
      setSelectedState(stateName);
      setDistrictGeoJson(gj);
      const bounds = L.geoJSON(gj).getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current?.fitBounds(bounds, { padding: [50, 50], maxZoom: 8, animate: true });
      }
    } catch (e) {
      console.error('Failed to load state districts:', e);
    } finally {
      setLayerLoading(false);
    }
  }, []);

  // ── loadRegionDistricts ─────────────────────────────────────────────
  const loadRegionDistricts = useCallback(async (regionName: string) => {
    if (!regionsData) return;
    const region = regionsData.regions?.find((r: any) =>
      r.name?.toLowerCase() === regionName.toLowerCase());
    if (!region) return;
    const stateName = region.primaryState || region.state;
    const path = STATE_DISTRICT_MAP[stateName];
    if (!path) return;
    setLayerLoading(true);
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const gj = await res.json();
      const filtered = gj.features.filter((f: any) => {
        const dn = getFeatureDistrictName(f.properties);
        return region.districts?.some((d: any) => {
          if (typeof d === 'string') return isDistrictMatch(dn, d);
          const nm = normalizeName(stateName), nm2 = normalizeName(d.stateName || '');
          return isDistrictMatch(dn, d.districtName) &&
            (nm === nm2 || nm.includes(nm2) || nm2.includes(nm));
        });
      });
      setSelectedRegion(regionName);
      setSelectedState(stateName);
      setDistrictGeoJson({ ...gj, features: filtered });
      const bounds = L.geoJSON({ ...gj, features: filtered }).getBounds();
      if (bounds.isValid()) {
        mapInstanceRef.current?.fitBounds(bounds, { padding: [50, 50], maxZoom: 8, animate: true });
      }
    } catch (e) {
      console.error('Failed to load region districts:', e);
    } finally {
      setLayerLoading(false);
    }
  }, [regionsData]);

  // Sync external selectedRegion prop
  useEffect(() => {
    if (mapMode === 'region' && selectedRegionProp && regionsData) {
      const region = regionsData.regions?.find((r: any) =>
        r.name === selectedRegionProp || r.id === selectedRegionProp);
      if (region && region.name !== selectedRegion) {
        loadRegionDistricts(region.name);
      }
    } else if (mapMode === 'region' && !selectedRegionProp && selectedRegion) {
      setSelectedRegion(null);
    } else if (mapMode === 'state' && selectedRegionProp && selectedRegionProp !== selectedState) {
      loadStateDistricts(selectedRegionProp);
    } else if (mapMode === 'state' && !selectedRegionProp && selectedState) {
      setSelectedState(null);
      setSelectedDistrict(null);
      setDistrictGeoJson(null);
      mapInstanceRef.current?.setView(INDIA_CENTER, INDIA_ZOOM, { animate: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegionProp, mapMode, regionsData]);

  // ── Derived ────────────────────────────────────────────────────────
  const choroplethData = useMemo((): RegionData[] => {
    const apiRegionData: RegionData[] = Array.isArray(roWiseData) ? roWiseData.map(ro => ({
      name: ro.roName, value: ro.schoolCount, region: ro.roName, roID: ro.roID,
    })) : [];
    if (currentMode === 'region') return apiRegionData.length > 0 ? apiRegionData : regionData;
    return regionData;
  }, [currentMode, roWiseData, regionData]);

  const hasFilters = !!(filters?.category && filters?.kpi);

  const visibleSchools = useMemo(() => {
    if (!showMarkersManually && !hasFilters && !selectedState && !selectedDistrict) return [];
    return allSchools.filter(s => {
      if (selectedDistrict) return s.districtName?.toLowerCase() === selectedDistrict.toLowerCase();
      if (selectedState) return s.stateName?.toLowerCase() === selectedState.toLowerCase();
      return true;
    });
  }, [allSchools, showMarkersManually, hasFilters, selectedState, selectedDistrict]);

  // ── Style keys (force GeoJSON re-render on selection change) ────────
  const stateStyleKey = `state|${selectedState}|${selectedDistrict}`;
  const regionViewStyleKey = `region|${selectedRegion}|${selectedState}`;

  const stateStyle = useCallback((feature: any): L.PathOptions => {
    const name = getFeatureStateName(feature?.properties);
    const isIsland = name.includes('Andaman') || name.includes('Nicobar') || name.includes('Lakshadweep');
    const isSelected = selectedState === name;
    const isDimmed = (selectedState || selectedDistrict) && !isSelected;
    return {
      fillColor: isDimmed ? (selectedDistrict ? '#e0e0e0' : '#D3D3D3') : getStateColor(name),
      weight: isIsland ? 3 : 2,
      opacity: isDimmed ? (selectedDistrict ? 0.3 : 0.5) : 1,
      color: isIsland ? '#000' : '#fff',
      fillOpacity: isDimmed ? (selectedDistrict ? 0.2 : 0.3) : 0.7,
    };
  }, [selectedState, selectedDistrict]);

  const districtStyle = useCallback((feature: any): L.PathOptions => {
    const name = getFeatureDistrictName(feature?.properties);
    const isSelected = selectedDistrict === name;
    const isDimmed = selectedDistrict && !isSelected;
    return {
      color: isDimmed ? '#999' : '#2c3e50',
      weight: isDimmed ? 1 : 2,
      opacity: isDimmed ? 0.3 : 1,
      fillOpacity: isDimmed ? 0.1 : 0.5,
      fillColor: isDimmed ? '#E5E5E5' : '#3498db',
    };
  }, [selectedDistrict]);

  const regionViewStyle = useCallback((feature: any): L.PathOptions => {
    const { regionColor, regionName, isRegional, stateName } = feature?.properties || {};
    const isIsland = stateName?.includes('Lakshadweep') || stateName?.includes('Andaman') || stateName?.includes('Nicobar');
    const isThisSelected = (selectedRegion && regionName === selectedRegion) || (selectedState && stateName === selectedState);
    const shouldDim = (selectedRegion || selectedState) && !isThisSelected;
    return {
      fillColor: shouldDim ? '#d0d0d0' : (isRegional ? regionColor : '#696969'),
      weight: isIsland ? 2.5 : 0.3,
      opacity: shouldDim ? 0.25 : (isRegional ? 1 : 0.5),
      color: isIsland ? '#000' : (shouldDim ? '#ccc' : '#fff'),
      fillOpacity: shouldDim ? 0.15 : (isRegional ? 0.7 : 0.4),
    };
  }, [selectedRegion, selectedState]);

  // ── onEachFeature handlers ──────────────────────────────────────────
  const onEachState = useCallback((feature: any, layer: L.Layer) => {
    const name = getFeatureStateName(feature.properties);
    const count = statesSchoolData.find(s => s.stateName === name)?.schoolCount;
    const label = count != null ? `${name} – ${count} schools` : name;
    (layer as L.Path).bindTooltip(label, { permanent: false, direction: 'center' });
    layer.on({
      click: () => {
        if (selectedState === name) {
          setSelectedState(null); setSelectedDistrict(null); setDistrictGeoJson(null);
          mapInstanceRef.current?.setView(INDIA_CENTER, INDIA_ZOOM, { animate: true });
          onRegionClick?.({ name: '', value: 0, state: '' });
        } else {
          loadStateDistricts(name);
          const val = regionData.find(d => d.state?.toLowerCase() === name.toLowerCase())?.value || 0;
          onRegionClick?.({ name, value: val, state: name });
        }
      },
      mouseover: () => setHoveredState(name),
      mouseout: () => setHoveredState(null),
    });
  }, [selectedState, loadStateDistricts, onRegionClick, choroplethData, statesSchoolData]);

  const onEachDistrict = useCallback((feature: any, layer: L.Layer) => {
    const name = getFeatureDistrictName(feature.properties);
    (layer as L.Path).bindTooltip(`<strong>${name}</strong>`, { permanent: false, direction: 'top' });
    layer.on({
      click: (e: any) => {
        L.DomEvent.stopPropagation(e);
        setSelectedDistrict(name);
        const bounds = (e.target as any).getBounds?.();
        if (bounds?.isValid()) {
          mapInstanceRef.current?.fitBounds(bounds, { padding: [50, 50], maxZoom: 10, animate: true });
        }
        onRegionClick?.({ name, value: 0, state: selectedState || '', district: name });
      },
    });
  }, [selectedState, onRegionClick]);

  const onEachRegionView = useCallback((feature: any, layer: L.Layer) => {
    const { regionName, isRegional, originalDistrictName } = feature.properties || {};
    const tip = isRegional
      ? `<strong>${regionName}</strong>`
      : `<strong>No Region</strong><br/>${originalDistrictName}`;
    (layer as L.Path).bindTooltip(tip, { permanent: false, direction: 'center' });
    layer.on({
      click: (e: any) => {
        L.DomEvent.stopPropagation(e);
        if (!isRegional || !regionName) return;
        loadRegionDistricts(regionName);
        onRegionClick?.({ name: regionName, value: 0, region: regionName });
      },
    });
  }, [loadRegionDistricts, onRegionClick]);

  // ── Actions ────────────────────────────────────────────────────────
  const resetZoom = () => {
    setSelectedState(null); setSelectedDistrict(null); setSelectedRegion(null);
    setDistrictGeoJson(null);
    mapInstanceRef.current?.setView(INDIA_CENTER, INDIA_ZOOM, { animate: true });
    onRegionClick?.({ name: '', value: 0, state: '' });
  };

  const handleModeToggle = (mode: 'state' | 'region') => {
    if (mode === currentMode) return;
    setCurrentMode(mode);
    setSelectedState(null); setSelectedDistrict(null); setSelectedRegion(null);
    setDistrictGeoJson(null);
    onModeChange?.(mode);
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <>
      <style nonce={CSP_NONCE}>{leafletStyles}</style>
      <div ref={wrapperRef} className="relative w-full rounded-lg shadow-lg bg-gray-50" style={{ height: 800 }}>

        {/* Global loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-[2000] bg-white flex items-center justify-center rounded-lg">
            <div className="text-center">
              <KVSLoader size={60} color="#6B1786" />
              <p className="text-gray-600 mt-4 font-medium">Loading map data...</p>
              <p className="text-sm text-gray-400 mt-1">This may take a moment...</p>
            </div>
          </div>
        )}

        {/* Layer loading overlay */}
        {layerLoading && !loading && (
          <div className="absolute inset-0 z-[1500] bg-black bg-opacity-30 flex items-center justify-center backdrop-blur-sm rounded-lg">
            <div className="bg-white rounded-lg shadow-xl p-6 text-center">
              <KVSLoader size={50} color="#2563EB" />
              <p className="text-gray-700 font-medium mt-3">Loading districts...</p>
            </div>
          </div>
        )}

        {/* School loading indicator */}
        {schoolLoading && !loading && !layerLoading && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1500]">
            <div className="bg-white rounded-lg shadow-lg p-4 border border-blue-200 animate-fadeIn flex items-center gap-3">
              <KVSLoader size={30} color="#2563EB" />
              <p className="text-gray-700 font-medium">Loading school locations...</p>
            </div>
          </div>
        )}

        {/* Region layer loading indicator */}
        {regionLayerLoading && !loading && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1500]">
            <div className="bg-white rounded-lg shadow-lg p-4 border border-purple-200 animate-fadeIn flex items-center gap-3">
              <KVSLoader size={30} color="#6B1786" />
              <p className="text-gray-700 font-medium">Loading region data...</p>
            </div>
          </div>
        )}

        {/* Mode toggle */}
        <div className="absolute top-4 left-4 z-[1000] bg-white rounded-lg shadow-lg overflow-hidden">
          {(['state', 'region'] as const).map((m, i) => (
            <button
              key={m}
              onClick={() => handleModeToggle(m)}
              className={`px-4 py-2 font-medium transition-colors text-sm ${i === 0 ? 'border-r border-gray-200' : ''} ${currentMode === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              {m === 'state' ? 'State View' : 'Region View'}
            </button>
          ))}
        </div>

        {/* Hover label */}
        {hoveredState && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg p-3 border pointer-events-none">
            <span className="font-semibold text-gray-800">{hoveredState}</span>
          </div>
        )}

        {/* Controls */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] flex gap-2">
          {(selectedState || selectedDistrict) && (
            <button
              onClick={() => {
                setSelectedDistrict(null); setSelectedState(null); setDistrictGeoJson(null);
                mapInstanceRef.current?.setView(INDIA_CENTER, INDIA_ZOOM, { animate: true });
              }}
              className="px-4 py-2 bg-white rounded-lg shadow text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-200"
            >
              Clear Selection
            </button>
          )}
          <button
            onClick={resetZoom}
            className="px-4 py-2 bg-white rounded-lg shadow text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-200"
          >
            Reset Zoom
          </button>
        </div>

        {/* Legend */}
        <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
          <button
            onClick={() => setShowMarkersManually(v => !v)}
            className={`px-4 py-2 rounded-lg shadow-lg font-medium text-sm transition-all border ${showMarkersManually ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
          >
            {showMarkersManually ? '✓ Markers Visible' : 'Show Markers'}
          </button>
          {(totalKVCount > 0 || pmShreeCount > 0) ? (
            <>
              <div className="bg-white rounded-lg shadow-lg p-3 border border-gray-200">
                <div className="text-xs text-gray-400 mb-1">Total Schools</div>
                <div className="text-2xl font-bold text-gray-800">{totalKVCount}</div>
              </div>
              <div className="bg-purple-50 rounded-lg shadow-lg p-3 border border-purple-200">
                <div className="text-xs text-purple-500 mb-1">PM SHRI</div>
                <div className="text-2xl font-bold text-purple-700">{pmShreeCount}</div>
              </div>
              <div className="bg-gray-50 rounded-lg shadow-lg p-3 border border-gray-200">
                <div className="text-xs text-gray-400 mb-1">KV (non PM SHRI)</div>
                <div className="text-xl font-bold text-gray-500">{Math.max(0, totalKVCount - pmShreeCount)}</div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-3 border border-gray-200">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <img src="/assets/PMShreeMarker.svg" width={22} height={22} alt="PM SHRI" />
                  <span className="text-sm text-gray-700">PM SHRI</span>
                </div>
                <div className="flex items-center gap-2">
                  <img src="/assets/kvMarker.svg" width={14} height={14} alt="KV" />
                  <span className="text-sm text-gray-700">KV</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <MapContainer
          center={INDIA_CENTER}
          zoom={INDIA_ZOOM}
          minZoom={4}
          maxZoom={12}
          maxBounds={INDIA_BOUNDS}
          maxBoundsViscosity={1.0}
          zoomControl={false}
          attributionControl={false}
          style={{ width: '100%', height: '100%', background: '#f8f9fa' }}
        >
          <ZoomControl position="topright" />
          <MapController onReady={(m) => { mapInstanceRef.current = m; }} />

          {/* State choropleth (no district drill-down) */}
          {currentMode === 'state' && stateGeoJson && !districtGeoJson && (
            <GeoJSON
              key={`states-${stateStyleKey}`}
              data={stateGeoJson}
              style={stateStyle}
              onEachFeature={onEachState}
            />
          )}

          {/* State background while district visible */}
          {currentMode === 'state' && stateGeoJson && districtGeoJson && (
            <GeoJSON
              key={`states-bg-${stateStyleKey}`}
              data={stateGeoJson}
              style={stateStyle}
              interactive={false}
            />
          )}

          {/* Region view */}
          {currentMode === 'region' && regionViewGeoJson && !districtGeoJson && (
            <GeoJSON
              key={`regionview-${regionViewStyleKey}`}
              data={regionViewGeoJson}
              style={regionViewStyle}
              onEachFeature={onEachRegionView}
            />
          )}

          {/* Region view background while district visible */}
          {currentMode === 'region' && regionViewGeoJson && districtGeoJson && (
            <GeoJSON
              key={`regionview-bg-${regionViewStyleKey}`}
              data={regionViewGeoJson}
              style={regionViewStyle}
              interactive={false}
            />
          )}

          {/* District drill-down */}
          {districtGeoJson && (
            <GeoJSON
              key={`districts-${selectedState}-${selectedDistrict}`}
              data={districtGeoJson}
              style={districtStyle}
              onEachFeature={onEachDistrict}
            />
          )}

          {/* School markers */}
          {visibleSchools.map((school, i) => {
            const lat = Number(school.latitude);
            const lng = Number(school.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            const isPM = Number(school.isPMShree) > 0;
            return (
              <Marker
                key={school.schoolID != null ? school.schoolID : `school-${i}`}
                position={[lat, lng]}
                icon={isPM ? pmIcon : kvIcon}
                eventHandlers={{
                  click: () => setSelectedSchool({
                    schoolID: school.schoolID,
                    schoolName: school.schoolName,
                    basicInfo: {
                      roName: school.roName,
                      stateName: school.stateName,
                      districtName: school.districtName,
                      isPMShree: school.isPMShree,
                      latitude: school.latitude,
                      longitude: school.longitude,
                    },
                  }),
                }}
              >
                <Tooltip direction="top" sticky>
                  <div style={{ minWidth: 180 }}>
                    <div><b>School:</b> {school.schoolName || '—'}</div>
                    <div><b>RO:</b> {school.roName || '—'}</div>
                    <div><b>State:</b> {school.stateName || '—'}</div>
                    <div><b>District:</b> {school.districtName || '—'}</div>
                    <div><b>Category:</b> {isPM ? 'PM SHRI' : 'KV'}</div>
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>

        {/* School detail popup */}
        {selectedSchool && (
          <SchoolPopup
            schoolID={selectedSchool.schoolID}
            schoolName={selectedSchool.schoolName}
            basicInfo={selectedSchool.basicInfo}
            onClose={() => setSelectedSchool(null)}
            onViewFullDetails={() => {}}
          />
        )}
      </div>
    </>
  );
}
